export type DeliveryChannel = 'sms' | 'email';
export type DeliveryMessageType = 'sms' | 'mms' | 'rcs';
export type DeliveryPreviewTabId = 'sms' | 'mms' | 'rcs' | 'email';

/** Rich-card fields for MMS or RCS (kept separate so images are not shared). */
export interface RichCardFormData {
  splitTwoBubbles: boolean;
  couponImageUrl: string;
  imageAltText: string;
  imageCaption: string;
  imageWidth: number;
  imageHeight: number;
  fallbackBehavior: string;
  fallbackText: string;
  bubble2TextBody: string;
}

export interface SmsEmailModalData {
  /** @deprecated Use smsChannelEnabled / emailChannelEnabled */
  channel: DeliveryChannel;
  smsChannelEnabled: boolean;
  emailChannelEnabled: boolean;
  messageType: DeliveryMessageType;
  messageBody: string;
  senderContact: string;
  smsLink: string;
  emailSubject: string;
  emailPreheader: string;
  emailBody: string;
  splitTwoBubbles: boolean;
  couponImageUrl: string;
  imageAltText: string;
  imageCaption: string;
  imageWidth: number;
  imageHeight: number;
  fallbackBehavior: string;
  fallbackText: string;
  bubble2TextBody: string;
  mmsRichCard?: RichCardFormData;
  rcsRichCard?: RichCardFormData;
}

export interface EmailConfigApi {
  body: string;
  subject: string;
  preheader: string;
}

export interface SmsConfigApi {
  sms_body: string;
}

export interface SmsDeliveryContentApi {
  platform: 'sms_delivery';
  sms_config: SmsConfigApi;
}

export interface DeliveryEmailContentApi {
  platform: 'email_delivery';
  status: boolean;
  email_config: EmailConfigApi;
}

export interface RichFallbackApi {
  text: string | null;
  behavior: string;
}

export interface RichBubble1CouponApi {
  image_width: number;
  image_height: number;
  image_caption: string | null;
  image_alt_text: string | null;
  coupon_image_url: string;
}

export interface MmsConfigApi {
  message_type: 'mms';
  mms_fallback: RichFallbackApi;
  bubble_1_coupon: RichBubble1CouponApi;
  bubble_2_text_body: string | null;
}

export interface RcsConfigApi {
  message_type: 'rcs';
  mms_fallback: RichFallbackApi;
  bubble_1_coupon: RichBubble1CouponApi;
  bubble_2_text_body: string | null;
}

export interface MmsContentApi {
  platform: 'mms';
  status: boolean;
  mms_config: MmsConfigApi;
}

export interface RcsContentApi {
  platform: 'rcs';
  rcs_config: RcsConfigApi;
}

/** Marketing Text Message tab content (`platform: sms`). */
export interface SmsContentApi {
  platform: 'sms';
  sms_sender_contact?: string;
  sms_body?: string;
  sms_link?: string;
}

/** Marketing Email tab content (`platform: email`, not `email_delivery`). */
export interface MarketingEmailContentApi {
  platform?: 'email';
  email_subject?: string;
  email_from_id?: string;
  email_logo_img?: string;
  email_body_img?: string;
  email_header?: string;
  email_body_text?: string;
  email_body_text2?: string;
  email_body_text3?: string;
  email_body_text4?: string;
  email_button_text?: string;
  email_button_position?: string;
  email_button_color?: string;
  email_footer_name?: string;
  email_footer_street?: string;
  email_footer_city?: string;
  email_footer_state?: string;
  email_footer_zip?: string;
  email_reply_to?: string;
  email_footer_phone?: string;
  email_config?: EmailConfigApi;
}

/** Facebook ad content (`platform: facebook`). */
export interface FacebookContentApi {
  platform?: 'facebook';
  fb_name?: string;
  fb_caption?: string;
  fb_headline?: string;
  fb_button_text?: string;
  fb_logo_img?: string;
  fb_img?: string;
}

/** Google Ads content (`platform: google`). */
export interface GoogleAdsContentApi {
  platform?: 'google';
  g_ad_img?: string;
  g_ad_title?: string;
  g_ad_company?: string;
}

export type DeliveryContentApi =
  | SmsDeliveryContentApi
  | MmsContentApi
  | RcsContentApi
  | DeliveryEmailContentApi;

export const MMS_FALLBACK_BEHAVIORS = [
  { value: 'send_sms_with_image_link', label: 'send_sms_with_image_link' },
  { value: 'send_sms_without_image', label: 'send_sms_without_image' }
] as const;

export const DEFAULT_EMAIL_DELIVERY = {
  emailSubject: 'Your offer is here',
  emailPreheader: 'Hi Alex,',
  emailBody: 'Your 20% off at Way is ready. Use code BF20OFF.'
} as const;

const DEFAULT_MMS_RICH_CARD_FIELDS: RichCardFormData = {
  splitTwoBubbles: true,
  couponImageUrl: '',
  imageAltText: 'Black Friday 20% off coupon',
  imageCaption: 'Black Friday ? save 20%',
  imageWidth: 640,
  imageHeight: 640,
  fallbackBehavior: 'send_sms_with_image_link',
  fallbackText: "Couldn't load image. View your coupon: {redemption_link}",
  bubble2TextBody:
    'Write your message here.'
};

export const DEFAULT_SMS_EMAIL_DATA: SmsEmailModalData = {
  channel: 'sms',
  smsChannelEnabled: true,
  emailChannelEnabled: false,
  messageType: 'sms',
  messageBody: 'Hey! Your 20% off at Champs is ready. Code: Grime20. Valid until 30th May.',
  senderContact: '+15551234567',
  smsLink: 'https://example.com/offer?sms=1',
  emailSubject: DEFAULT_EMAIL_DELIVERY.emailSubject,
  emailPreheader: DEFAULT_EMAIL_DELIVERY.emailPreheader,
  emailBody: DEFAULT_EMAIL_DELIVERY.emailBody,
  ...DEFAULT_MMS_RICH_CARD_FIELDS
};

export const DEFAULT_MMS_RICH_CARD: RichCardFormData = {
  ...DEFAULT_MMS_RICH_CARD_FIELDS
};

export const DEFAULT_RCS_RICH_CARD: RichCardFormData = {
  splitTwoBubbles: true,
  couponImageUrl: '',
  imageAltText: 'RCS spring offer',
  imageCaption: 'Tap to redeem',
  imageWidth: 1200,
  imageHeight: 628,
  fallbackBehavior: 'send_sms_without_image',
  fallbackText: 'Your RCS offer: https://example.com/offer',
  bubble2TextBody: 'Book your wash today ? offer expires soon.'
};

/** Default copy for create-modal placeholders and save-time fallbacks. */
export const SMS_EMAIL_PLACEHOLDERS: SmsEmailModalData = { ...DEFAULT_SMS_EMAIL_DATA };

export const EMPTY_SMS_EMAIL_DATA: SmsEmailModalData = {
  channel: 'sms',
  smsChannelEnabled: true,
  emailChannelEnabled: false,
  messageType: 'sms',
  messageBody: '',
  senderContact: '',
  smsLink: '',
  emailSubject: '',
  emailPreheader: '',
  emailBody: '',
  splitTwoBubbles: true,
  couponImageUrl: '',
  imageAltText: '',
  imageCaption: '',
  imageWidth: DEFAULT_SMS_EMAIL_DATA.imageWidth,
  imageHeight: DEFAULT_SMS_EMAIL_DATA.imageHeight,
  fallbackBehavior: DEFAULT_SMS_EMAIL_DATA.fallbackBehavior,
  fallbackText: '',
  bubble2TextBody: ''
};

export const EMPTY_MMS_RICH_CARD: RichCardFormData = {
  splitTwoBubbles: true,
  couponImageUrl: '',
  imageAltText: '',
  imageCaption: '',
  imageWidth: DEFAULT_MMS_RICH_CARD.imageWidth,
  imageHeight: DEFAULT_MMS_RICH_CARD.imageHeight,
  fallbackBehavior: DEFAULT_MMS_RICH_CARD.fallbackBehavior,
  fallbackText: '',
  bubble2TextBody: ''
};

export const EMPTY_RCS_RICH_CARD: RichCardFormData = {
  splitTwoBubbles: true,
  couponImageUrl: '',
  imageAltText: '',
  imageCaption: '',
  imageWidth: DEFAULT_RCS_RICH_CARD.imageWidth,
  imageHeight: DEFAULT_RCS_RICH_CARD.imageHeight,
  fallbackBehavior: DEFAULT_RCS_RICH_CARD.fallbackBehavior,
  fallbackText: '',
  bubble2TextBody: ''
};

export const EMPTY_EMAIL_FIELDS = {
  emailSubject: '',
  emailPreheader: '',
  emailBody: ''
} as const;

export function clearRichCardFieldsFromForm(data: SmsEmailModalData): SmsEmailModalData {
  return {
    ...data,
    splitTwoBubbles: true,
    couponImageUrl: '',
    imageAltText: '',
    imageCaption: '',
    imageWidth: DEFAULT_MMS_RICH_CARD.imageWidth,
    imageHeight: DEFAULT_MMS_RICH_CARD.imageHeight,
    fallbackBehavior: DEFAULT_MMS_RICH_CARD.fallbackBehavior,
    fallbackText: '',
    bubble2TextBody: ''
  };
}

export function clearEmailFieldsFromForm(data: SmsEmailModalData): SmsEmailModalData {
  return {
    ...data,
    emailSubject: '',
    emailPreheader: '',
    emailBody: ''
  };
}

export function clearSmsFieldsFromForm(data: SmsEmailModalData): SmsEmailModalData {
  return {
    ...clearRichCardFieldsFromForm(data),
    messageType: 'sms',
    messageBody: '',
    senderContact: '',
    smsLink: ''
  };
}

export function pickRichCardFields(data: SmsEmailModalData): RichCardFormData {
  return {
    splitTwoBubbles: data.splitTwoBubbles,
    couponImageUrl: data.couponImageUrl || '',
    imageAltText: data.imageAltText || '',
    imageCaption: data.imageCaption || '',
    imageWidth: data.imageWidth,
    imageHeight: data.imageHeight,
    fallbackBehavior: data.fallbackBehavior || '',
    fallbackText: data.fallbackText || '',
    bubble2TextBody: data.bubble2TextBody || ''
  };
}

export function applyRichCardFields(
  data: SmsEmailModalData,
  rich: RichCardFormData
): SmsEmailModalData {
  return {
    ...data,
    splitTwoBubbles: rich.splitTwoBubbles,
    couponImageUrl: rich.couponImageUrl,
    imageAltText: rich.imageAltText,
    imageCaption: rich.imageCaption,
    imageWidth: rich.imageWidth,
    imageHeight: rich.imageHeight,
    fallbackBehavior: rich.fallbackBehavior,
    fallbackText: rich.fallbackText,
    bubble2TextBody: rich.bubble2TextBody
  };
}

export function richCardFromMmsApi(api: MmsContentApi): RichCardFormData {
  const config = api.mms_config;
  return {
    splitTwoBubbles: true,
    couponImageUrl: config?.bubble_1_coupon?.coupon_image_url || '',
    imageAltText: config?.bubble_1_coupon?.image_alt_text || '',
    imageCaption: config?.bubble_1_coupon?.image_caption || '',
    imageWidth: config?.bubble_1_coupon?.image_width ?? 640,
    imageHeight: config?.bubble_1_coupon?.image_height ?? 640,
    fallbackBehavior: config?.mms_fallback?.behavior || 'send_sms_with_image_link',
    fallbackText: config?.mms_fallback?.text || '',
    bubble2TextBody: config?.bubble_2_text_body || ''
  };
}

export function richCardFromRcsApi(api: RcsContentApi): RichCardFormData {
  const config = api.rcs_config;
  return {
    splitTwoBubbles: true,
    couponImageUrl: config?.bubble_1_coupon?.coupon_image_url || '',
    imageAltText: config?.bubble_1_coupon?.image_alt_text || '',
    imageCaption: config?.bubble_1_coupon?.image_caption || '',
    imageWidth: config?.bubble_1_coupon?.image_width ?? 1200,
    imageHeight: config?.bubble_1_coupon?.image_height ?? 628,
    fallbackBehavior: config?.mms_fallback?.behavior || 'send_sms_without_image',
    fallbackText: config?.mms_fallback?.text || '',
    bubble2TextBody: config?.bubble_2_text_body || ''
  };
}

export function getMmsRichCardForApi(data: SmsEmailModalData): RichCardFormData {
  if (data.messageType === 'mms') {
    return pickRichCardFields(data);
  }
  return data.mmsRichCard ?? { ...DEFAULT_MMS_RICH_CARD };
}

export function getRcsRichCardForApi(data: SmsEmailModalData): RichCardFormData {
  if (data.messageType === 'rcs') {
    return pickRichCardFields(data);
  }
  return data.rcsRichCard ?? { ...DEFAULT_RCS_RICH_CARD };
}

export function mapSmsDeliveryModalToApi(data: SmsEmailModalData): SmsDeliveryContentApi {
  return {
    platform: 'sms_delivery',
    sms_config: {
      sms_body: data.messageBody?.trim() || ''
    }
  };
}

/** @deprecated Use mapSmsDeliveryModalToApi for optional-settings delivery SMS. */
export function mapSmsModalToApi(data: SmsEmailModalData): SmsDeliveryContentApi {
  return mapSmsDeliveryModalToApi(data);
}

export function mapMmsModalToApi(data: SmsEmailModalData): MmsContentApi {
  const rich = getMmsRichCardForApi(data);
  return {
    platform: 'mms',
    status: true,
    mms_config: {
      message_type: 'mms',
      mms_fallback: {
        text: rich.fallbackText || null,
        behavior: rich.fallbackBehavior || 'send_sms_with_image_link'
      },
      bubble_1_coupon: {
        image_width: Number(rich.imageWidth) || 640,
        image_height: Number(rich.imageHeight) || 640,
        image_caption: rich.imageCaption || null,
        image_alt_text: rich.imageAltText || null,
        coupon_image_url: rich.couponImageUrl || ''
      },
      bubble_2_text_body: rich.bubble2TextBody || null
    }
  };
}

export function mapDeliveryEmailModalToApi(data: SmsEmailModalData): DeliveryEmailContentApi {
  return {
    platform: 'email_delivery',
    status: true,
    email_config: {
      subject: data.emailSubject?.trim() || '',
      preheader: data.emailPreheader?.trim() || '',
      body: data.emailBody?.trim() || ''
    }
  };
}

export function mapDeliveryEmailApiToModal(api: DeliveryEmailContentApi): Partial<SmsEmailModalData> {
  const config = api.email_config;
  return {
    emailChannelEnabled: true,
    channel: 'email',
    emailSubject: config?.subject || '',
    emailPreheader: config?.preheader || '',
    emailBody: config?.body || ''
  };
}

export function hasEmailDeliveryApiContent(email?: DeliveryEmailContentApi | null): boolean {
  if (!email?.email_config) {
    return false;
  }
  const config = email.email_config;
  return Boolean(config.subject?.trim() || config.preheader?.trim() || config.body?.trim());
}

export function isDeliveryEmailContent(item: any): item is DeliveryEmailContentApi {
  const record = item as { platform?: string; email_config?: EmailConfigApi };
  if (!record?.email_config) {
    return false;
  }
  return record.platform === 'email_delivery' || record.platform === 'email';
}

export function findDeliveryEmailContent(contents: any): DeliveryEmailContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find(isDeliveryEmailContent) || null;
  }
  const record = contents as Record<string, any>;
  const nestedDelivery = record['delivery_email'] ?? record['email_delivery'];
  if (isDeliveryEmailContent(nestedDelivery)) {
    return nestedDelivery;
  }
  const nestedEmail = record['email'];
  if (isDeliveryEmailContent(nestedEmail)) {
    return nestedEmail;
  }
  if (isDeliveryEmailContent(record)) {
    return record as any as DeliveryEmailContentApi;
  }
  return null;
}

export function isSmsDeliveryContent(item: any): item is SmsDeliveryContentApi {
  const record = item as { platform?: string; sms_config?: SmsConfigApi };
  if (record?.platform === 'sms_delivery') {
    return true;
  }
  return record?.platform === 'sms' && Boolean(record.sms_config);
}

export function findSmsDeliveryContent(contents: any): SmsDeliveryContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find(isSmsDeliveryContent) || null;
  }
  const record = contents as Record<string, any>;
  const nested = record['sms_delivery'];
  if (isSmsDeliveryContent(nested)) {
    return nested;
  }
  if (isSmsDeliveryContent(record)) {
    return record as any as SmsDeliveryContentApi;
  }
  return null;
}

/** Marketing Text Message tab — `platform: sms` only (not `sms_delivery`). */
export function findSmsContent(contents: any): SmsContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return (
      contents.find(
        (item: SmsContentApi) => item?.platform === 'sms' && !isSmsDeliveryContent(item)
      ) || null
    );
  }
  const record = contents as Record<string, any>;
  const sms = record['sms'];
  if (sms && typeof sms === 'object' && !isSmsDeliveryContent(sms)) {
    return sms as SmsContentApi;
  }
  if (record['platform'] === 'sms' && !isSmsDeliveryContent(record)) {
    return record as any as SmsContentApi;
  }
  return null;
}

export function isMarketingEmailContent(item: any): item is MarketingEmailContentApi {
  const record = item as MarketingEmailContentApi;
  if (!record || typeof record !== 'object') {
    return false;
  }
  if (isDeliveryEmailContent(record)) {
    return false;
  }
  return record.platform === 'email';
}

/** Marketing Email tab — `platform: email` only (not `email_delivery`). */
export function findEmailContent(contents: any): MarketingEmailContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find(isMarketingEmailContent) || null;
  }
  const record = contents as Record<string, any>;
  const email = record['email'];
  if (isMarketingEmailContent(email)) {
    return email;
  }
  if (isMarketingEmailContent(record)) {
    return record as MarketingEmailContentApi;
  }
  return null;
}

/** Facebook ad tab — `platform: facebook`. */
export function findFacebookContent(contents: any): FacebookContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find((item: FacebookContentApi) => item?.platform === 'facebook') || null;
  }
  const record = contents as Record<string, any>;
  const facebook = record['facebook'];
  if (facebook && typeof facebook === 'object') {
    return facebook as FacebookContentApi;
  }
  if (record['platform'] === 'facebook') {
    return record as FacebookContentApi;
  }
  return null;
}

/** Google Ads tab — `platform: google`. */
export function findGoogleAdsContent(contents: any): GoogleAdsContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find((item: GoogleAdsContentApi) => item?.platform === 'google') || null;
  }
  const record = contents as Record<string, any>;
  const google = record['google'];
  if (google && typeof google === 'object') {
    return google as GoogleAdsContentApi;
  }
  if (record['platform'] === 'google') {
    return record as GoogleAdsContentApi;
  }
  return null;
}

/** Maps delivery email API entry to modal/preview fields only. */
export function mapDeliveryEmailToPreviewData(
  api: DeliveryEmailContentApi
): Pick<SmsEmailModalData, 'emailSubject' | 'emailPreheader' | 'emailBody' | 'emailChannelEnabled'> {
  const fields = mapDeliveryEmailApiToModal(api);
  return {
    emailChannelEnabled: true,
    emailSubject: fields.emailSubject || '',
    emailPreheader: fields.emailPreheader || '',
    emailBody: fields.emailBody || ''
  };
}

export function normalizeChannelFlags(data: Partial<SmsEmailModalData>): Pick<
  SmsEmailModalData,
  'smsChannelEnabled' | 'emailChannelEnabled' | 'channel'
> {
  let sms = data.smsChannelEnabled;
  let email = data.emailChannelEnabled;
  if (sms === undefined && email === undefined) {
    if (data.channel === 'email') {
      sms = false;
      email = true;
    } else {
      sms = true;
      email = false;
    }
  } else {
    sms = sms ?? true;
    email = email ?? false;
  }
  if (!sms && !email) {
    sms = true;
  }
  const channel: DeliveryChannel = email && !sms ? 'email' : 'sms';
  return { smsChannelEnabled: sms, emailChannelEnabled: email, channel };
}

export function mapRcsModalToApi(data: SmsEmailModalData): RcsContentApi {
  const rich = getRcsRichCardForApi(data);
  return {
    platform: 'rcs',
    rcs_config: {
      message_type: 'rcs',
      mms_fallback: {
        text: rich.fallbackText || null,
        behavior: rich.fallbackBehavior || 'send_sms_without_image'
      },
      bubble_1_coupon: {
        image_width: Number(rich.imageWidth) || 1200,
        image_height: Number(rich.imageHeight) || 628,
        image_caption: rich.imageCaption || null,
        image_alt_text: rich.imageAltText || null,
        coupon_image_url: rich.couponImageUrl || ''
      },
      bubble_2_text_body: rich.bubble2TextBody || null
    }
  };
}

export function mapSmsDeliveryApiToModal(api: SmsDeliveryContentApi): SmsEmailModalData {
  const body = api.sms_config?.sms_body ?? '';
  return {
    ...EMPTY_SMS_EMAIL_DATA,
    channel: 'sms',
    messageType: 'sms',
    messageBody: body,
    senderContact: '',
    smsLink: '',
    mmsRichCard: { ...EMPTY_MMS_RICH_CARD },
    rcsRichCard: { ...EMPTY_RCS_RICH_CARD }
  };
}

/** @deprecated Use mapSmsDeliveryApiToModal for optional-settings delivery SMS. */
export function mapSmsApiToModal(api: SmsDeliveryContentApi | SmsContentApi): SmsEmailModalData {
  if (isSmsDeliveryContent(api)) {
    return mapSmsDeliveryApiToModal(api);
  }
  const legacy = api as SmsContentApi;
  return {
    ...DEFAULT_SMS_EMAIL_DATA,
    channel: 'sms',
    messageType: 'sms',
    messageBody: legacy.sms_body || '',
    senderContact: legacy.sms_sender_contact || '',
    smsLink: legacy.sms_link || ''
  };
}

export function mapMmsApiToModal(api: MmsContentApi): SmsEmailModalData {
  const mmsRichCard = richCardFromMmsApi(api);
  return applyRichCardFields(
    {
      ...DEFAULT_SMS_EMAIL_DATA,
      channel: 'sms',
      messageType: 'mms',
      messageBody: mmsRichCard.bubble2TextBody,
      mmsRichCard,
      rcsRichCard: { ...DEFAULT_RCS_RICH_CARD }
    },
    mmsRichCard
  );
}

export function mapRcsApiToModal(api: RcsContentApi): SmsEmailModalData {
  const rcsRichCard = richCardFromRcsApi(api);
  return applyRichCardFields(
    {
      ...DEFAULT_SMS_EMAIL_DATA,
      channel: 'sms',
      messageType: 'rcs',
      messageBody: rcsRichCard.bubble2TextBody,
      mmsRichCard: { ...DEFAULT_MMS_RICH_CARD },
      rcsRichCard
    },
    rcsRichCard
  );
}

export function hasSmsDeliveryApiContent(sms?: SmsDeliveryContentApi | SmsContentApi | null): boolean {
  if (!sms) {
    return false;
  }
  if (isSmsDeliveryContent(sms)) {
    return Boolean(sms.sms_config?.sms_body?.trim());
  }
  const legacy = sms as SmsContentApi;
  return Boolean(legacy.sms_body?.trim() || legacy.sms_sender_contact?.trim());
}

export function hasMmsDeliveryApiContent(mms?: MmsContentApi | null): boolean {
  if (!mms) {
    return false;
  }
  const config = mms.mms_config;
  return Boolean(
    config?.bubble_2_text_body?.trim() ||
    config?.bubble_1_coupon?.coupon_image_url?.trim() ||
    config?.mms_fallback?.text?.trim()
  );
}

export function hasRcsDeliveryApiContent(rcs?: RcsContentApi | null): boolean {
  if (!rcs) {
    return false;
  }
  const config = rcs.rcs_config;
  return Boolean(
    config?.bubble_2_text_body?.trim() ||
    config?.bubble_1_coupon?.coupon_image_url?.trim() ||
    config?.mms_fallback?.text?.trim()
  );
}

export function findMmsContent(contents: any): MmsContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find((item: MmsContentApi) => item?.platform === 'mms') || null;
  }
  const record = contents as Record<string, any>;
  if (record['platform'] === 'mms') {
    return record as any as MmsContentApi;
  }
  const mms = record['mms'];
  if (mms && typeof mms === 'object') {
    return mms as MmsContentApi;
  }
  return null;
}

export function findRcsContent(contents: any): RcsContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find((item: RcsContentApi) => item?.platform === 'rcs') || null;
  }
  const record = contents as Record<string, any>;
  if (record['platform'] === 'rcs') {
    return record as any as RcsContentApi;
  }
  const rcs = record['rcs'];
  if (rcs && typeof rcs === 'object') {
    return rcs as RcsContentApi;
  }
  return null;
}

export function findDeliveryContent(contents: any): {
  sms: SmsDeliveryContentApi | null;
  mms: MmsContentApi | null;
  rcs: RcsContentApi | null;
} {
  return {
    sms: findSmsDeliveryContent(contents),
    mms: findMmsContent(contents),
    rcs: findRcsContent(contents)
  };
}

/** Maps campaign contents to modal state, keeping SMS/MMS/RCS data separate (no shared images). */
export function mapDeliveryContentsToModal(contents: any): SmsEmailModalData {
  const sms = findSmsDeliveryContent(contents);
  const mms = findMmsContent(contents);
  const rcs = findRcsContent(contents);
  const deliveryEmail = findDeliveryEmailContent(contents);
  const hasSms = hasSmsDeliveryApiContent(sms);
  const hasMms = hasMmsDeliveryApiContent(mms);
  const hasRcs = hasRcsDeliveryApiContent(rcs);
  const hasEmail = hasEmailDeliveryApiContent(deliveryEmail);

  const mmsRichCard = hasMms ? richCardFromMmsApi(mms!) : { ...EMPTY_MMS_RICH_CARD };
  const rcsRichCard = hasRcs ? richCardFromRcsApi(rcs!) : { ...EMPTY_RCS_RICH_CARD };

  const smsFields = hasSms
    ? mapSmsDeliveryApiToModal(sms!)
    : {
        ...EMPTY_SMS_EMAIL_DATA,
        messageType: 'sms' as const,
        channel: 'sms' as const,
        mmsRichCard: { ...EMPTY_MMS_RICH_CARD },
        rcsRichCard: { ...EMPTY_RCS_RICH_CARD }
      };

  const activeType: DeliveryMessageType = hasMms ? 'mms' : hasRcs ? 'rcs' : 'sms';
  const activeRich = activeType === 'mms' ? mmsRichCard : activeType === 'rcs' ? rcsRichCard : null;

  const channelFlags = normalizeChannelFlags({
    smsChannelEnabled: hasSms || hasMms || hasRcs,
    emailChannelEnabled: hasEmail,
    channel: hasEmail && !(hasSms || hasMms || hasRcs) ? 'email' : 'sms'
  });

  const emailFields = hasEmail
    ? mapDeliveryEmailApiToModal(deliveryEmail!)
    : { ...EMPTY_EMAIL_FIELDS };

  const base: SmsEmailModalData = {
    ...smsFields,
    ...channelFlags,
    ...emailFields,
    messageType: activeType,
    mmsRichCard,
    rcsRichCard
  };

  return activeRich ? applyRichCardFields(base, activeRich) : base;
}

/** @deprecated Prefer mapDeliveryContentsToModal when multiple delivery platforms may exist. */
export function mapDeliveryApiToModal(contents: any): SmsEmailModalData {
  return mapDeliveryContentsToModal(contents);
}

function isSameRichCard(a: RichCardFormData, b: RichCardFormData): boolean {
  return (
    a.splitTwoBubbles === b.splitTwoBubbles &&
    (a.couponImageUrl || '').trim() === (b.couponImageUrl || '').trim() &&
    (a.imageAltText || '').trim() === (b.imageAltText || '').trim() &&
    (a.imageCaption || '').trim() === (b.imageCaption || '').trim() &&
    Number(a.imageWidth) === Number(b.imageWidth) &&
    Number(a.imageHeight) === Number(b.imageHeight) &&
    (a.fallbackBehavior || '').trim() === (b.fallbackBehavior || '').trim() &&
    (a.fallbackText || '').trim() === (b.fallbackText || '').trim() &&
    (a.bubble2TextBody || '').trim() === (b.bubble2TextBody || '').trim()
  );
}

/** Compares only fields relevant to each enabled channel (SMS + Email can be edited independently). */
export function isSameFormData(a: SmsEmailModalData, b: SmsEmailModalData): boolean {
  const aChannels = normalizeChannelFlags(a);
  const bChannels = normalizeChannelFlags(b);

  if (
    aChannels.smsChannelEnabled !== bChannels.smsChannelEnabled ||
    aChannels.emailChannelEnabled !== bChannels.emailChannelEnabled
  ) {
    return false;
  }

  if (aChannels.emailChannelEnabled) {
    if ((a.emailSubject || '').trim() !== (b.emailSubject || '').trim()) {
      return false;
    }
    if ((a.emailPreheader || '').trim() !== (b.emailPreheader || '').trim()) {
      return false;
    }
    if ((a.emailBody || '').trim() !== (b.emailBody || '').trim()) {
      return false;
    }
  }

  if (!aChannels.smsChannelEnabled) {
    return true;
  }

  if (a.messageType !== b.messageType) {
    return false;
  }

  if (a.messageType === 'sms') {
    return (
      (a.messageBody || '').trim() === (b.messageBody || '').trim() &&
      (a.senderContact || '').trim() === (b.senderContact || '').trim() &&
      (a.smsLink || '').trim() === (b.smsLink || '').trim()
    );
  }

  const aRich =
    a.messageType === 'mms'
      ? a.mmsRichCard ?? pickRichCardFields(a)
      : a.rcsRichCard ?? pickRichCardFields(a);
  const bRich =
    b.messageType === 'mms'
      ? b.mmsRichCard ?? pickRichCardFields(b)
      : b.rcsRichCard ?? pickRichCardFields(b);
  return isSameRichCard(aRich, bRich) && a.splitTwoBubbles === b.splitTwoBubbles;
}
